import { useCallback, useMemo, useState } from 'react';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Reflect01Icon } from '@/components/icons/Reflect01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { RegenerateAIDPMenuItem } from '@/modules/aiDP/RegenerateAIDPMenuItem';
import { RegenerateDPSuggestionsV2MenuItem } from '@/modules/aiSuggestions/components/RegenerateDPSuggestionsV2MenuItem';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { CopyDispositiveProvisionsModal } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/CopyDispositiveProvisionsModal/CopyDispositiveProvisionsModal';
import { CopyDispositionsResult } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/CopyDispositiveProvisionsModal/CopyDispositiveProvisionsModal.types';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  SplitDispositionModal,
  SplitDispositionModalResult,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal';
import { RemoveAllRecipientsMenuItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/RemoveAllRecipientsMenuItem';
import { ToggleGrowAllByInflationMenuItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ToggleGrowAllByInflationMenuItem';
import { ToggleResiduaryPaysTaxesMenuItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ToggleResiduaryPaysTaxesMenuItem';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { useCurrentRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/formHooks';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { COLORS } from '@/styles/tokens/colors';
import { PathsOf } from '@/types/subform';

interface PrimaryClientDeathSectionContextMenuProps {
  deathEvent: PrimaryClientDeathEvent;
  onBulkAddRecipients?: (
    recipients: DispositiveProvisionsFormRecipient[]
  ) => void;
}

export function PrimaryClientDeathSectionContextMenu({
  deathEvent,
  onBulkAddRecipients,
}: PrimaryClientDeathSectionContextMenuProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();

  const { showFeedback } = useFeedback();

  const { setValue } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const currentRecipients = useCurrentRecipients({ deathEvent });

  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);

  const dispositiveProvisionsContext = useDispositiveProvisionsContext();
  const { primaryClients } = dispositiveProvisionsContext;
  const useKGV2DPSuggestions = useFeatureFlag('aikg_v2_associations_poc');

  // Calculate hasStateTax
  const { availableStateEstateTaxes } = useIRSConstants();
  const hasStateTax = useMemo(() => {
    // Check if any primary client has state estate tax
    return primaryClients.some(
      (client) =>
        client.stateCode &&
        availableStateEstateTaxes?.includes(client.stateCode)
    );
  }, [primaryClients, availableStateEstateTaxes]);

  const handleCopyDispositionsResult = useCallback(
    (result: CopyDispositionsResult | null) => {
      if (!result) return;

      const recipientsPath = `${getDeathEventBasePath(
        deathEvent
      )}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

      const templateIdPath = `${getDeathEventBasePath(
        deathEvent
      )}.templateId` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

      setValue(recipientsPath, result.recipients, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(templateIdPath, result.templateId ?? null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      showFeedback('Dispositions copied', { variant: 'success' });
    },
    [deathEvent, setValue, showFeedback]
  );

  const handleCopyModalClose = useCallback(
    (result: CopyDispositionsResult | null) => {
      setIsCopyModalOpen(false);
      handleCopyDispositionsResult(result);
    },
    [handleCopyDispositionsResult]
  );

  const handleCopyDispositions = useCallback(() => {
    setIsCopyModalOpen(true);
  }, []);

  const handleSplitResult = useCallback(
    (result: SplitDispositionModalResult | null) => {
      setIsSplitModalOpen(false);
      if (!result) return;

      onBulkAddRecipients?.(result.recipients);
    },
    [onBulkAddRecipients]
  );

  const handleOpenSplitModal = useCallback(() => {
    setIsSplitModalOpen(true);
  }, []);

  if (!canEditHouseholdData) {
    return null;
  }

  return (
    <>
      {isCopyModalOpen && (
        <CopyDispositiveProvisionsModal
          isOpen={isCopyModalOpen}
          onClose={handleCopyModalClose}
          hasRecipients={Boolean(currentRecipients?.length)}
        />
      )}
      {isSplitModalOpen && (
        <SplitDispositionModal
          isOpen={isSplitModalOpen}
          onClose={handleSplitResult}
          hasStateTax={hasStateTax}
          deathEvent={deathEvent}
        />
      )}
      <ContextMenuButton
        data-testid="DispositiveProvisions-menu"
        size="xs"
        variant="transparent"
      >
        {useKGV2DPSuggestions ? (
          <RegenerateDPSuggestionsV2MenuItem key="regen-ai-dp-menu-item" />
        ) : (
          <RegenerateAIDPMenuItem key="regen-ai-dp-menu-item" />
        )}
        <MenuItem
          key="copy-dispositions"
          icon={<Copy07Icon size={16} color={COLORS.GRAY['500']} />}
          label="Copy dispositions from another entity"
          onClick={handleCopyDispositions}
          muiMenuItemProps={{
            divider: false,
          }}
        />
        <MenuItem
          key="split-disposition"
          icon={<Reflect01Icon size={16} color={COLORS.GRAY['500']} />}
          label="Create evenly split disposition"
          onClick={handleOpenSplitModal}
          muiMenuItemProps={{
            divider: true,
          }}
        />
        <ToggleResiduaryPaysTaxesMenuItem deathEvent={deathEvent} />
        <ToggleGrowAllByInflationMenuItem
          recipients={currentRecipients ?? []}
          recipientsBasePath={getDeathEventBasePath(deathEvent)}
        />
        <RemoveAllRecipientsMenuItem deathEvent={deathEvent} />
      </ContextMenuButton>
    </>
  );
}
